import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';
import FileSaver from 'file-saver';
import NotificationToast from '../components/common/NotificationToast';
import print from 'print-js';

export interface ApiFetcher {
  get(endpoint: string, options?: AxiosRequestConfig): Promise<any>;

  post(endpoint: string, dataToPost: any, options?: AxiosRequestConfig): Promise<any>;

  put(endpoint: string, dataToPost: any, options?: AxiosRequestConfig): Promise<any>;

  delete(endpoint: string, id?: number | string, options?: AxiosRequestConfig): void;

  deleteList(endpoint: string, ids: number[] | string, options?: AxiosRequestConfig): void;

  patch(endpoint: string, data: any, options?: AxiosRequestConfig): Promise<any>;

  downloadFile(
    url: string,
    options?: AxiosRequestConfig,
    extension?: string,
    exportName?: string,
    onSuccess?: () => void,
    onError?: () => void,
  ): void;

  postFile(endpoint: string, data: any, options?: AxiosRequestConfig): Promise<any>;

  putFile(endpoint: string, data: any, options?: AxiosRequestConfig): Promise<any>;

  token: string;

  prepareParams: (params: any) => string;
}

interface ApiFetcherConfig {
  apiUrl: string;
}

export class ApiFetcherImpl implements ApiFetcher {
  get token(): string {
    return this._token;
  }

  set token(value: string) {
    this._token = value;
  }

  constructor(private config: ApiFetcherConfig) {
    axios.defaults.paramsSerializer = this.prepareParams;
    axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const errorMessage =
          error && error.response && error.response.data && error.response.data.Message
            ? error.response.data.Message
            : error;

        return Promise.reject({ error, errorMessage });
      },
    );
  }

  private _token: string;

  prepareParams = (params: any) => qs.stringify(params, { allowDots: true });

  private combineUrl(endpoint: string): string {
    return `${this.config.apiUrl}${endpoint}`;
  }

  private getOptions(options?: AxiosRequestConfig): AxiosRequestConfig {
    let additionnalHeaders: { Authorization?: string } = {};
    if (this._token) {
      additionnalHeaders = {
        Authorization: `Bearer ${this._token}`,
      };
    }
    return {
      withCredentials: false,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...additionnalHeaders,
      },
      ...options,
    };
  }

  public async get(endpoint: string, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);
      const response = await axios.get(this.combineUrl(endpoint), finalOptions);
      return response.data;
    } catch (error) {
      console.log(error);

      //@ts-ignore
      NotificationToast.showError(error.error.error.message); // throw error;
    }
  }

  public async post(endpoint: string, dataToPost: any, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);
      const response = await axios.post(this.combineUrl(endpoint), dataToPost, finalOptions);
      return response.data;
    } catch (error) {
      //@ts-ignore
      NotificationToast.showError(error.error.error.message);
      throw error;
    }
  }

  public async put(endpoint: string, dataToPost: any, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);

      const response = await axios.put(this.combineUrl(endpoint), dataToPost, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async delete(endpoint: string, id?: number | string, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);

      let url = this.combineUrl(endpoint);
      if (id) {
        url += '/' + id;
      }

      const response = await axios.delete(url, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async deleteList(endpoint: string, ids: number[] | string, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions({
        data: ids,
        ...options,
      });

      const response = await axios.delete(this.combineUrl(endpoint), finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async patch(endpoint: string, data: any, options?: AxiosRequestConfig): Promise<any> {
    try {
      const finalOptions = this.getOptions(options);

      const response = await axios.patch(this.combineUrl(endpoint), data, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async downloadFile(
    url: string,
    options?: AxiosRequestConfig,
    extension?: string,
    exportName?: string,
    onSuccess?: () => void,
    onError?: () => void,
  ) {
    let additionnalHeaders: { AuthToken?: string } = {};
    if (this.token) {
      additionnalHeaders = {
        AuthToken: `${this.token}`,
      };
    }
    const finalOptions = {
      // Commented because if not commented we have an error with Cors
      // withCredentials: true,
      headers: {
        Accept: 'application/octet-stream',
        ...additionnalHeaders,
      },
      responseType: 'blob',
      ...options,
    };

    const promise = axios({
      url: this.combineUrl(url),
      method: 'GET',
      ...finalOptions,
    });
    promise
      .then((response) => {
        let suggestedFileName;
        const disposition = response.headers['content-disposition'];
        if (!exportName && disposition && disposition.indexOf('attachment') !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) {
            suggestedFileName = matches[1].replace(/['"]/g, '');
          }
        }
        const effectiveFileName = suggestedFileName
          ? suggestedFileName
          : exportName
          ? `${exportName}.${extension}`
          : `download.${extension}`;
        console.log(
          "Received header ['content-disposition']: " +
            suggestedFileName +
            ', effective fileName: ' +
            effectiveFileName,
        );

        FileSaver.saveAs(response.data, effectiveFileName);
        var pdfFile = new Blob([response.data], {
          type: 'application/pdf',
        });
        var pdfUrl = URL.createObjectURL(pdfFile);
        print(pdfUrl);
        if (onSuccess) onSuccess();
      })
      .catch((response) => {
        if (onError) onError();
        console.error('Could not Download the report from the backend.', response);
      });
    return promise;
  }

  public async postFile(endpoint: string, data: any, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);

      const response = await axios.post(this.combineUrl(endpoint), data, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async putFile(endpoint: string, data: any, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);

      const response = await axios.put(this.combineUrl(endpoint), data, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}
